<template>
  <b-modal
    :model-value="visible"
    @update:model-value="$emit('update:visible', $event)"
    :title="modalTitle"
    size="xl"
    dialog-class="history-dialog"
    hide-footer
    modal-class="history-modal"
  >
    <template #title>
      {{ modalTitle }}
      <modal-help topic="record-history" />
    </template>
    <div v-if="loading" class="text-center py-4">
      <span class="text-muted">Loading history...</span>
    </div>
    <div v-else-if="entries.length === 0" class="text-center py-4">
      <span class="text-muted">No history entries found.</span>
    </div>
    <div v-else>
      <table class="table table-sm history-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Action</th>
            <th>Record ID</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in entries" :key="entry._id">
            <td class="history-cell-time">{{ formatTime(entry.timestamp) }}</td>
            <td>
              <span class="history-action-badge" :class="'history-action-' + entry.action">{{ entry.action }}</span>
            </td>
            <td class="history-cell-id">{{ entry.recordId }}</td>
            <td class="history-cell-details">
              <template v-if="entry.details && entry.details.tampered">
                <span class="history-detail-summary">This history entry was changed and its values were not shown.</span>
              </template>
              <template v-else-if="entry.action === 'update' && entry.details && entry.details.fields">
                <div class="history-field-changes">
                  <div v-for="field in entry.details.fields" :key="field" class="history-field-change">
                    <span class="history-field-name">{{ field }}</span>
                    <span class="history-field-arrow">&rarr;</span>
                    <history-value v-if="entry.details.before" kind="old" :preview="imagePreview(entry.details.before, field)" :text="formatValue(entry.details.before[field])" />
                    <span class="history-field-arrow">&rarr;</span>
                    <history-value v-if="entry.details.after" kind="new" :preview="imagePreview(entry.details.after, field)" :text="formatValue(entry.details.after[field])" />
                  </div>
                </div>
              </template>
              <template v-else-if="entry.action === 'create' && entry.details && entry.details.after">
                <div class="history-field-changes">
                  <div v-for="field in userFields(entry.details.after)" :key="field" class="history-field-change">
                    <span class="history-field-name">{{ field }}</span>
                    <span class="history-field-arrow">=</span>
                    <history-value kind="new" :preview="imagePreview(entry.details.after, field)" :text="formatValue(entry.details.after[field])" />
                  </div>
                </div>
              </template>
              <template v-else-if="entry.action === 'delete' && entry.details && entry.details.before">
                <div class="history-field-changes">
                  <div v-for="field in userFields(entry.details.before)" :key="field" class="history-field-change">
                    <span class="history-field-name">{{ field }}</span>
                    <span class="history-field-arrow">=</span>
                    <history-value kind="old" :preview="imagePreview(entry.details.before, field)" :text="formatValue(entry.details.before[field])" />
                  </div>
                </div>
              </template>
              <template v-else-if="entry.action === 'link' && entry.details">
                <span class="history-detail-summary">Linked via <strong>{{ entry.details.column }}</strong> to record #{{ entry.details.targetId }}</span>
              </template>
              <template v-else-if="entry.action === 'unlink' && entry.details">
                <span class="history-detail-summary">Unlinked from <strong>{{ entry.details.column }}</strong> record #{{ entry.details.targetId }}</span>
              </template>
              <template v-else-if="entry.action === 'authenticator_reset'">
                <span class="history-detail-summary">Authenticator cleared by <strong>{{ entry.details && entry.details.clearedBy }}</strong>. The secret is not stored here. They set one up again the next time they sign in.</span>
              </template>
              <template v-else>
                <span class="text-muted">—</span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="history-pagination" v-if="totalPages > 1">
        <b-button size="sm" variant="outline-secondary" :disabled="page <= 1" @click="goToPage(page - 1)">Prev</b-button>
        <span class="history-page-info">Page {{ page }} of {{ totalPages }} ({{ total }} entries)</span>
        <b-button size="sm" variant="outline-secondary" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { historyImagePreview } from '../../../shared/history-image.js'

const HistoryValue = {
  name: 'HistoryValue',
  props: {
    preview: { type: Object, default: null },
    text: { type: String, default: '' },
    kind: { type: String, default: 'new' }
  },
  template: `
    <img v-if="preview && preview.src" class="history-thumb" :src="preview.src" alt="" />
    <span v-else-if="preview && preview.omitted" class="history-thumb-missing">image not stored</span>
    <span v-else :class="kind === 'old' ? 'history-field-old' : 'history-field-new'">{{ text }}</span>
  `
}

export default {
  components: { HistoryValue },
  props: {
    visible: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    recordId: { type: Number, default: null },
    applicationUniqueTitle: { type: String, default: '' }
  },
  emits: ['update:visible'],
  data () {
    return {
      entries: [],
      page: 1,
      pageSize: 25,
      total: 0,
      totalPages: 0,
      loading: false
    }
  },
  computed: {
    modalTitle () {
      if (this.recordId != null) {
        return `History — Record #${this.recordId}`
      }
      return `History — ${this.spreadSheetUniqueTitle}`
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.page = 1
        this.loadHistory()
      }
    }
  },
  methods: {
    async loadHistory () {
      if (!this.spreadSheetUniqueTitle) return

      this.loading = true

      try {
        let url
        if (this.recordId != null) {
          url = `${VUE_APP_SERVER_URL}/record-history/${this.spreadSheetUniqueTitle}/${this.recordId}`
        } else {
          url = `${VUE_APP_SERVER_URL}/record-history/${this.spreadSheetUniqueTitle}`
        }

        url += `?page=${this.page}&pageSize=${this.pageSize}`

        const response = await axios.get(url, {
          headers: { 'application-unique-title': this.applicationUniqueTitle },
          withCredentials: false
        })

        this.entries = response.data.entries || []
        this.total = response.data.total || 0
        this.totalPages = response.data.totalPages || 0
        this.page = response.data.page || 1
      } catch (err) {
        console.warn('Failed to load history:', err)
        this.entries = []
      }

      this.loading = false
    },
    goToPage (newPage) {
      this.page = newPage
      this.loadHistory()
    },
    formatTime (timestamp) {
      if (!timestamp) return '—'
      try {
        const d = new Date(timestamp)
        return d.toLocaleString()
      } catch {
        return timestamp
      }
    },
    userFields (obj) {
      if (!obj || typeof obj !== 'object') return []
      const skip = new Set(['id', 'createdAt', 'updatedAt', '_id'])
      return Object.keys(obj).filter(k => !skip.has(k) && obj[k] != null && obj[k] !== '')
    },
    formatValue (val) {
      if (val == null) return '(empty)'
      if (typeof val === 'object') return JSON.stringify(val)
      const s = String(val)
      return s.length > 240 ? s.substring(0, 240) + '...' : s
    },
    imagePreview (side, field) {
      return historyImagePreview(side, field)
    }
  }
}
</script>

<style>
.history-modal .modal-dialog,
.history-dialog {
  max-width: calc(100vw - 2rem);
  width: calc(100vw - 2rem);
  margin-left: auto;
  margin-right: auto;
}

.history-modal .modal-content {
  background-color: #1e1e1b;
  color: #e8e8e6;
  border-color: #474842;
}

.history-modal .modal-header {
  background-color: #181916;
  border-color: #474842;
  color: #e8e8e6;
}

.history-modal .modal-header .btn-close {
  filter: invert(1);
}

.history-modal .modal-body {
  padding: 0;
}

.history-table {
  --bs-table-bg: #1e1e1b;
  --bs-table-color: #e8e8e6;
  --bs-table-border-color: #2e2e2b;
  margin-bottom: 0;
}

.history-table thead th {
  background-color: #242421 !important;
  color: #c8c8c6 !important;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 10px 14px !important;
  border-color: #2e2e2b !important;
  position: sticky;
  top: 0;
  z-index: 1;
}

.history-table tbody td {
  color: #e8e8e6 !important;
  background-color: #1e1e1b !important;
  font-size: 0.85rem;
  padding: 8px 14px !important;
  border-color: #2e2e2b !important;
  vertical-align: top;
}

.history-table tbody tr:nth-of-type(odd) td {
  background-color: #222220 !important;
}

.history-table tbody tr:hover td {
  background-color: #2a2a27 !important;
}

.history-cell-time {
  white-space: nowrap;
  color: #8a8a88 !important;
  font-size: 0.82rem !important;
  min-width: 160px;
}

.history-cell-id {
  font-family: monospace;
  color: #9ad12b !important;
  font-size: 0.85rem !important;
}

.history-action-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-action-create {
  background-color: rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

.history-action-update {
  background-color: rgba(77, 171, 247, 0.2);
  color: #4dabf7;
}

.history-action-delete {
  background-color: rgba(244, 67, 54, 0.2);
  color: #ef5350;
}

.history-action-bulk_create {
  background-color: rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

.history-action-link {
  background-color: rgba(156, 39, 176, 0.2);
  color: #ce93d8;
}

.history-action-unlink {
  background-color: rgba(255, 152, 0, 0.2);
  color: #ffb74d;
}

.history-field-changes {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-field-change {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.history-field-name {
  font-weight: 600;
  color: #b0b0ae;
  font-size: 0.82rem;
}

.history-field-arrow {
  color: #a8a8a4;
  font-size: 0.8rem;
}

.history-field-old {
  color: #ef5350;
  font-size: 0.82rem;
  text-decoration: line-through;
  max-width: 36rem;
  overflow-wrap: anywhere;
  white-space: normal;
}

.history-field-new {
  color: #66bb6a;
  font-size: 0.82rem;
  max-width: 36rem;
  overflow-wrap: anywhere;
  white-space: normal;
}

.history-thumb {
  width: 72px;
  height: 72px;
  object-fit: contain;
  background: #111;
  border: 1px solid #3a3a37;
  border-radius: 4px;
}

.history-thumb-missing {
  color: #8a8a88;
  font-size: 0.75rem;
}

.history-detail-summary {
  color: #8a8a88;
  font-size: 0.85rem;
}

.history-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 12px 14px;
  border-top: 1px solid #2e2e2b;
  background-color: #1a1a17;
}

.history-page-info {
  font-size: 0.82rem;
  color: #8a8a88;
}

.history-pagination .btn-outline-secondary {
  color: #b0b0ae;
  border-color: #474842;
  font-size: 0.8rem;
  padding: 3px 14px;
}

.history-pagination .btn-outline-secondary:hover:not(:disabled) {
  background-color: #2a2a27;
  border-color: #a8a8a4;
  color: #e8e8e6;
}

.history-pagination .btn-outline-secondary:disabled {
  color: #3e3e3c;
  border-color: #2e2e2b;
}
</style>
